import api from './axiosConfig';
import { toApiTimeValue } from '../utils/timeUtils';

const toApiTime = (time: string): string => {
  return toApiTimeValue(time);
};

export const shiftApi = {
  // Work Week Configuration (document-aligned API)
  getWorkWeekConfiguration: async (): Promise<any[]> => {
    const response = await api.get('/workweek');
    return response.data;
  },

  getWorkWeekOptions: async (): Promise<Array<{ id: string; name: string }>> => {
    const response = await api.get('/workweek');
    return (response.data || []).map((w: any) => ({
      id: w.id,
      name: w.name
    }));
  },

  saveWorkWeekConfiguration: async (data: {
    name: string;
    isActive?: boolean;
    days: Array<{
      day: string;
      workingDay: boolean;
      weekOff: boolean;
      halfDay: boolean;
    }>;
    weekOffRules?: Array<{
      day: string;
      weeks: number[];
    }>;
  }): Promise<{ id: string }> => {
    const response = await api.post<{ id: string }>('/workweek', data);
    return response.data;
  },

  updateWorkWeekConfiguration: async (
    id: string,
    data: {
      name: string;
      isActive?: boolean;
      days: Array<{
        day: string;
        workingDay: boolean;
        weekOff: boolean;
        halfDay: boolean;
      }>;
      weekOffRules?: Array<{
        day: string;
        weeks: number[];
      }>;
    }
  ): Promise<void> => {
    await api.put(`/workweek/${id}`, data);
  },

  // Shifts
  getShifts: async (tenantId?: string): Promise<any[]> => {
    const response = await api.get('/shifts', {
      params: tenantId ? { tenantId } : undefined,
    });
    return (response.data || []).map((s: any) => ({
      id: s.id,
      shiftName: s.name,
      startTime: s.startTime,
      endTime: s.endTime,
      breakDuration: s.breakDurationMinutes,
      graceTime: s.gracePeriodMinutes,
      isActive: s.isActive,
      isAssigned: Boolean(s.isAssigned),
    }));
  },

  createShift: async (data: {
    shiftName: string;
    startTime: string;
    endTime: string;
    breakDuration: number;
    gracePeriod: number;
  }): Promise<{ id: string }> => {
    const response = await api.post<{ id: string }>('/shifts', {
      name: data.shiftName,
      startTime: toApiTime(data.startTime),
      endTime: toApiTime(data.endTime),
      breakDurationMinutes: data.breakDuration,
      gracePeriodMinutes: data.gracePeriod,
    });
    return response.data;
  },

  updateShift: async (id: string, data: {
    shiftName: string;
    startTime: string;
    endTime: string;
    breakDuration: number;
    gracePeriod: number;
    isActive?: boolean;
  }): Promise<void> => {
    await api.put(`/shifts/${id}`, {
      name: data.shiftName,
      startTime: toApiTime(data.startTime),
      endTime: toApiTime(data.endTime),
      breakDurationMinutes: data.breakDuration,
      gracePeriodMinutes: data.gracePeriod,
      isActive: data.isActive,
    });
  },

  deleteShift: async (id: string): Promise<void> => {
    await api.delete(`/shifts/${id}`);
  },

  // Work Weeks
  getWorkWeeks: async (): Promise<any[]> => {
    const response = await api.get('/shifts/work-weeks');
    return (response.data || []).map((w: any) => {
      const workingDays = (w.days || []).filter((d: any) => d.isWorkingDay).map((d: any) => d.dayName);
      const weeklyOff = (w.days || []).filter((d: any) => !d.isWorkingDay).map((d: any) => d.dayName);
      return {
        id: w.id,
        name: w.name,
        isActive: w.isActive,
        workingDays,
        weeklyOff,
        days: w.days || [],
      };
    });
  },

  createWorkWeek: async (data: {
    name: string;
    workingDays: string[];
    weeklyOff: string[];
  }): Promise<{ id: string }> => {
    const allDays = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];
    const days = allDays.map((day, index) => ({
      dayOfWeek: index + 1,
      isWorkingDay: data.workingDays.includes(day),
    }));

    const response = await api.post<{ id: string }>('/shifts/work-weeks', {
      name: data.name,
      days,
    });
    return response.data;
  },

  updateWorkWeek: async (id: string, data: {
    name: string;
    workingDays: string[];
    weeklyOff: string[];
  }): Promise<void> => {
    const allDays = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];
    const days = allDays.map((day, index) => ({
      dayOfWeek: index + 1,
      isWorkingDay: data.workingDays.includes(day),
    }));

    await api.put(`/shifts/work-weeks/${id}`, {
      id,
      name: data.name,
      isActive: true,
      days,
    });
  },

  // Employee Shift Mapping
  mapEmployeeToShift: async (data: {
    employeeId: string;
    shiftId: string;
    departmentId?: string;
    effectiveDate?: string;
  }): Promise<{ id: string }> => {
    const response = await api.post<{ id: string }>('/shifts/employee-shifts/assign', {
      employeeId: data.employeeId,
      shiftId: data.shiftId,
      effectiveDate: data.effectiveDate || new Date().toISOString().split('T')[0],
    });
    return response.data;
  },

  bulkMapEmployeeToShift: async (data: Array<{
    employeeId: string;
    shiftId: string;
    effectiveDate: string;
  }>): Promise<{ ids: string[] }> => {
    const response = await api.post<{ ids: string[] }>('/shifts/employee-shifts/bulk-assign', data);
    return response.data;
  },

  mapEmployeeToWorkWeek: async (data: {
    employeeId: string;
    workWeekId: string;
    effectiveDate?: string;
  }): Promise<{ id: string }> => {
    const response = await api.post<{ id: string }>('/shifts/employee-workweeks/assign', {
      employeeId: data.employeeId,
      workWeekId: data.workWeekId,
      effectiveDate: data.effectiveDate || new Date().toISOString().split('T')[0]
    });
    return response.data;
  },

  // Assign shift with effective date
  assignShiftToEmployee: async (data: {
    employeeId: string;
    shiftId: string;
    effectiveDate: string;
  }): Promise<{ id: string }> => {
    const response = await api.post<{ id: string }>('/shifts/employee-shifts/assign', data);
    return response.data;
  },

  // Employees with shifts
  getEmployeesWithShifts: async (filters?: {
    search?: string;
    department?: string;
    shift?: string;
  }): Promise<any[]> => {
    const response = await api.get('/shifts/employee-shifts');
    return (response.data || []).map((x: any) => ({
      id: x.id,
      employeeId: x.employeeId,
      employeeName: x.employeeName,
      shiftId: x.shiftId,
      shiftName: x.shiftName,
      workWeekId: x.workWeekId,
      workWeekName: x.workWeekName,
      startTime: x.startTime,
      endTime: x.endTime,
      effectiveDate: x.effectiveDate,
      isCurrent: x.isCurrent,
    }));
  },

  deleteEmployeeShift: async (id: string): Promise<void> => {
    await api.delete(`/shifts/employee-shifts/${id}`);
  },

  // Attendance Rules
  getAttendanceRules: async (): Promise<any> => {
    const response = await api.get('/attendance/rules');
    return response.data;
  },

  saveAttendanceRules: async (data: {
    minWorkingHours: number;
    lateArrivalGrace: number;
    lateArrivalPenalty: string;
    earlyCheckoutGrace: number;
    earlyCheckoutPenalty: string;
    overtimeEnabled: boolean;
    maxOvertimeHours: number;
  }): Promise<void> => {
    await api.put('/attendance/rules', data);
  },

  // Advance Leave Settings
  getAdvanceSettings: async (): Promise<any> => {
    const response = await api.get('/attendance/advance-settings');
    return response.data;
  },

  saveAdvanceSettings: async (data: {
    halfDayEnabled: boolean;
    halfDayHours: number;
    accrualEnabled: boolean;
    accrualFrequency: string;
    accrualRate: number;
    carryForwardEnabled: boolean;
    maxCarryForward: number;
    carryForwardExpiry: number;
    negativeLeavesEnabled: boolean;
  }): Promise<void> => {
    await api.put('/attendance/advance-settings', data);
  },

  // Import biometric logs
  importBiometricLogs: async (file: File): Promise<{ imported: number }> => {
    const formData = new FormData();
    formData.append('file', file);
    const response = await api.post<{ imported: number }>('/attendance/import-logs', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return response.data;
  },

  // Process attendance for a date
  processAttendance: async (date: string): Promise<{ processed: number }> => {
    const response = await api.post<{ processed: number }>('/attendance/process', { date });
    return response.data;
  },

  // Save auto-process schedule
  saveAutoProcessSchedule: async (data: {
    runTime: string;
    enabled: boolean;
  }): Promise<void> => {
    await api.put('/attendance/auto-process-schedule', data);
  },
};
